import { parseExpiryEpochSec, resolveInstanceUrl, withQuery } from '../instance/urls.ts';
import type { StoryboardSpec, SubtitleTrackRef } from '../model/media.ts';
import type { VideoDetails, VideoSummary } from '../model/video.ts';
import { err, ok, parseError, type ParsedList, type ParseError, type Result } from '../result.ts';
import { resolveCaptionUrl, type ParseContext } from './context.ts';
import { parseImageList } from './image.ts';
import { parseDescriptionNodes } from './richText.ts';
import {
  asArray,
  asBoolean,
  asIntLike,
  asNonEmptyString,
  asNumber,
  asObject,
  asString,
  asStringArray,
  field,
  firstPresent,
  type RawObject,
} from './raw.ts';
import { asEpochSeconds } from './time.ts';

/**
 * Scrub-preview sheets are fetched whole, so the widest ladder rung is not automatically the best
 * one: `320×180` for `dQw4w9WgXcQ` means 12 sprite sheets against 5 at `160×90`. Prefer the
 * largest rung that is still ≤ this width.
 */
const PREFERRED_STORYBOARD_WIDTH = 160;

const AUTO_GENERATED_RE = /\(auto-generated\)/i;

/**
 * Handles both spellings of the same field: `languageCode` from `/api/v1/captions/{id}` and
 * `language_code` from the `captions[]` array embedded in `/api/v1/videos/{id}` (§2). A parser
 * hard-coded to one reads `undefined` from the other — no error, just a missing language tag.
 */
export function parseSubtitleTrack(raw: unknown, ctx: ParseContext): Result<SubtitleTrackRef, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('SubtitleTrackRef', null, 'wrong-type'));

  const rawUrl = asNonEmptyString(field(source, 'url'));
  if (rawUrl === null) return err(parseError('SubtitleTrackRef', 'url', 'missing'));

  const url = resolveCaptionUrl(ctx, rawUrl);
  if (url === null) return err(parseError('SubtitleTrackRef', 'url', 'malformed'));

  const label = asString(field(source, 'label')) ?? '';
  const languageCode = asString(firstPresent(source, ['languageCode', 'language_code'])) ?? '';

  return ok({ label, languageCode, url, autoGenerated: AUTO_GENERATED_RE.test(label) });
}

function parseSubtitleList(raw: unknown, ctx: ParseContext): ParsedList<SubtitleTrackRef> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [] };
  const items: SubtitleTrackRef[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parseSubtitleTrack(entry, ctx);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }
  return { items, dropped };
}

/** `GET /api/v1/captions/{id}` — the standalone list endpoint, `{ captions: [...] }`. */
export function parseCaptionTrackList(raw: unknown, ctx: ParseContext): ParsedList<SubtitleTrackRef> {
  const source = asObject(raw);
  if (!source) return { items: [], dropped: [parseError('CaptionList', null, 'wrong-type')] };
  return parseSubtitleList(field(source, 'captions'), ctx);
}

function parseStoryboard(raw: unknown, ctx: ParseContext): StoryboardSpec | null {
  const array = asArray(raw);
  if (!array) return null;

  let best: { source: RawObject; width: number } | null = null;
  for (const entry of array) {
    const source = asObject(entry);
    if (!source) continue;
    // Live and DVR videos report `count: -1` / `storyboardCount: -1`: there is no finished
    // thumbnail track to fetch, so there is no storyboard.
    const count = asIntLike(field(source, 'count'));
    if (count === null || count <= 0) continue;
    if (asNonEmptyString(field(source, 'url')) === null) continue;

    const width = asIntLike(field(source, 'width')) ?? 0;
    if (best === null) {
      best = { source, width };
      continue;
    }
    const bestFits = best.width <= PREFERRED_STORYBOARD_WIDTH;
    const candidateFits = width <= PREFERRED_STORYBOARD_WIDTH;
    if (candidateFits && (!bestFits || width > best.width)) best = { source, width };
    else if (!candidateFits && !bestFits && width < best.width) best = { source, width };
  }
  if (best === null) return null;

  const rawUrl = asNonEmptyString(field(best.source, 'url'));
  if (rawUrl === null) return null;
  const vttUrl = resolveInstanceUrl(ctx.config, rawUrl);
  if (vttUrl === null) return null;

  return {
    vttUrl,
    width: asIntLike(field(best.source, 'width')),
    height: asIntLike(field(best.source, 'height')),
    count: asIntLike(field(best.source, 'count')),
    intervalMs: asIntLike(field(best.source, 'interval')),
  };
}

/**
 * §2 — signed media URLs expire ~5h45m out, and the expiry is only ever on the *stream* URLs, not
 * on the instance's manifest URL. Take the earliest across everything the payload offers so
 * recovery is scheduled against the first thing that will actually 403.
 */
function parseMediaExpiry(source: RawObject, hlsUrl: string | null): number | null {
  let earliest: number | null = null;
  const consider = (url: string | null): void => {
    if (url === null) return;
    const expiry = parseExpiryEpochSec(url);
    if (expiry === null) return;
    if (earliest === null || expiry < earliest) earliest = expiry;
  };

  for (const key of ['adaptiveFormats', 'formatStreams']) {
    const array = asArray(field(source, key)) ?? [];
    for (const entry of array) {
      const stream = asObject(entry);
      if (!stream) continue;
      consider(asNonEmptyString(field(stream, 'url')));
    }
  }
  consider(hlsUrl);
  return earliest;
}

/**
 * `formatStreams` holds exactly one entry — itag 18, 360p muxed (§2). The URL it carries points
 * straight at googlevideo, which is IP-locked to the *instance's* egress and sends no usable CORS
 * headers, so it is never the URL to hand a player. `/latest_version?…&local=true` is the proxied
 * equivalent, verified 206 `video/mp4` with `Range` honoured.
 */
function buildProgressiveFallbackUrl(source: RawObject, videoId: string, ctx: ParseContext): string | null {
  const array = asArray(field(source, 'formatStreams')) ?? [];
  let itag: number | null = null;
  for (const entry of array) {
    const stream = asObject(entry);
    if (!stream) continue;
    const candidate = asIntLike(field(stream, 'itag'));
    if (candidate === null) continue;
    if (candidate === 18) {
      itag = 18;
      break;
    }
    if (itag === null) itag = candidate;
  }
  if (itag === null) return null;

  const base = resolveInstanceUrl(ctx.config, '/latest_version');
  if (base === null) return null;
  return withQuery(base, { id: videoId, itag, local: 'true' });
}

/** Transport-resolved with `?local=true` applied — the only form that can actually be played (§2). */
function resolveManifestUrl(raw: unknown, ctx: ParseContext): string | null {
  const rawUrl = asNonEmptyString(raw);
  if (rawUrl === null) return null;
  const resolved = resolveInstanceUrl(ctx.config, rawUrl);
  if (resolved === null) return null;
  // `hlsUrl` already carries a query, so this cannot be string concatenation (R2).
  return withQuery(resolved, { local: 'true' });
}

/**
 * The abbreviated record: `recommendedVideos[]`, search results, `/api/v1/popular`.
 * Malformed entries drop themselves (R4) rather than failing the list they are in.
 */
export function parseVideoSummary(raw: unknown, ctx: ParseContext): Result<VideoSummary, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('VideoSummary', null, 'wrong-type'));

  const videoId = asNonEmptyString(field(source, 'videoId'));
  if (videoId === null) return err(parseError('VideoSummary', 'videoId', 'missing'));

  return ok({
    videoId,
    title: asString(field(source, 'title')) ?? '',
    author: asString(field(source, 'author')) ?? '',
    authorId: asNonEmptyString(field(source, 'authorId')),
    authorUrl: asNonEmptyString(field(source, 'authorUrl')),
    authorVerified: asBoolean(field(source, 'authorVerified')) ?? false,
    lengthSeconds: asIntLike(field(source, 'lengthSeconds')) ?? 0,
    publishedEpochSec: asEpochSeconds(field(source, 'published')),
    // R7: `viewCountText` is server-locale-bound and deliberately ignored, which is why this is
    // `null` for recommendations — they carry no numeric view count at all.
    viewCount: asNumber(field(source, 'viewCount')),
    thumbnails: parseImageList(field(source, 'videoThumbnails'), ctx),
    liveNow: asBoolean(field(source, 'liveNow')) ?? false,
    isUpcoming: asBoolean(field(source, 'isUpcoming')) ?? false,
    premiereAtEpochSec: asEpochSeconds(field(source, 'premiereTimestamp')),
  });
}

export function parseVideoSummaryList(raw: unknown, ctx: ParseContext): ParsedList<VideoSummary> {
  const array = asArray(raw);
  if (!array) return { items: [], dropped: [] };
  const items: VideoSummary[] = [];
  const dropped: ParseError[] = [];
  for (const entry of array) {
    const parsed = parseVideoSummary(entry, ctx);
    if (parsed.ok) items.push(parsed.value);
    else dropped.push(parsed.error);
  }
  return { items, dropped };
}

/**
 * R4 — `GET /api/v1/videos/{id}`. Unknown fields ignored, missing optionals become `null`, and
 * the only thing that fails the whole parse is a payload with no usable `videoId`.
 *
 * Note what is *not* read: `viewCountText`, `publishedText`, `subCountText` (R7), and `type`.
 * `type` is not a reliable discriminator — `chapters-SqcY0GlETPk` is an ordinary finished VOD
 * that reports `type: "scheduled"`. The booleans are the truth.
 */
export function parseVideoDetails(raw: unknown, ctx: ParseContext): Result<VideoDetails, ParseError> {
  const source = asObject(raw);
  if (!source) return err(parseError('VideoDetails', null, 'wrong-type'));

  const videoId = asNonEmptyString(field(source, 'videoId'));
  if (videoId === null) return err(parseError('VideoDetails', 'videoId', 'missing'));

  const description = asString(field(source, 'description')) ?? '';
  const lengthSeconds = asIntLike(field(source, 'lengthSeconds')) ?? 0;
  const hlsUrl = resolveManifestUrl(field(source, 'hlsUrl'), ctx);
  const subtitles = parseSubtitleList(field(source, 'captions'), ctx);
  const recommended = parseVideoSummaryList(field(source, 'recommendedVideos'), ctx);

  return ok({
    videoId,
    title: asString(field(source, 'title')) ?? '',
    description,
    // R8 — the tree, not the HTML. `descriptionHtml` is read here and discarded: it contributes
    // only the mention→channel map, because its anchor *positions* are unreliable (richText.ts).
    descriptionNodes: parseDescriptionNodes(
      description,
      asString(field(source, 'descriptionHtml')) ?? '',
      { durationSec: lengthSeconds },
    ),
    author: asString(field(source, 'author')) ?? '',
    authorId: asNonEmptyString(field(source, 'authorId')),
    authorUrl: asNonEmptyString(field(source, 'authorUrl')),
    authorVerified: asBoolean(field(source, 'authorVerified')) ?? false,
    authorThumbnails: parseImageList(field(source, 'authorThumbnails'), ctx),
    publishedEpochSec: asEpochSeconds(field(source, 'published')),
    lengthSeconds,
    viewCount: asNumber(field(source, 'viewCount')),
    likeCount: asNumber(field(source, 'likeCount')),
    dislikeCount: asNumber(field(source, 'dislikeCount')),
    rating: asNumber(field(source, 'rating')),
    genre: asNonEmptyString(field(source, 'genre')),
    keywords: asStringArray(field(source, 'keywords')),
    isFamilyFriendly: asBoolean(field(source, 'isFamilyFriendly')) ?? true,
    allowedRegions: asStringArray(field(source, 'allowedRegions')),
    isListed: asBoolean(field(source, 'isListed')) ?? true,
    allowRatings: asBoolean(field(source, 'allowRatings')) ?? true,
    paid: asBoolean(field(source, 'paid')) ?? false,
    premium: asBoolean(field(source, 'premium')) ?? false,
    liveNow: asBoolean(field(source, 'liveNow')) ?? false,
    isUpcoming: asBoolean(field(source, 'isUpcoming')) ?? false,
    isPostLiveDvr: asBoolean(field(source, 'isPostLiveDvr')) ?? false,
    premiereAtEpochSec: asEpochSeconds(field(source, 'premiereTimestamp')),
    thumbnails: parseImageList(field(source, 'videoThumbnails'), ctx),
    storyboard: parseStoryboard(field(source, 'storyboards'), ctx),
    subtitles: subtitles.items,
    dashUrl: resolveManifestUrl(field(source, 'dashUrl'), ctx),
    hlsUrl,
    progressiveFallbackUrl: buildProgressiveFallbackUrl(source, videoId, ctx),
    mediaExpiresAtEpochSec: parseMediaExpiry(source, hlsUrl),
    recommended: recommended.items,
    // R4 — both sub-lists drop malformed entries, and until now both discarded the count with
    // them. A caption track that resolved to no usable URL (the probe found no caption prefix)
    // is the realistic case, and "this video has no subtitles" must stay distinguishable from
    // "we threw its subtitles away".
    dropped: [...subtitles.dropped, ...recommended.dropped],
  });
}
